<template>
  <div>
    <!-- First Recharge Popup -->
    <van-popup
      :value="value"
      @input="$emit('input', $event)"
      round
      :close-on-click-overlay="true"
      class="fr-popup-wrapper"
    >
      <div class="fr-popup">
        <button class="fr-popup__close" @click="handleClose">
          <van-icon name="cross" size="18" color="rgba(255,255,255,0.4)" />
        </button>

        <!-- Header -->
        <div class="fr-popup__header">
          <div class="fr-popup__grid-bg"></div>
          <p class="fr-popup__mission">{{ $lang.fr_limited_offer || 'Limited Time Offer' }}</p>
          <p class="fr-popup__subtitle">
            {{ $lang.fr_title_short || 'First' }} <span class="fr-popup__highlight">{{ $lang.fr_bonus || 'Recharge' }}</span>
          </p>

          <!-- Countdown: before purchase → buy deadline; after purchase → next claim unlock -->
          <div v-if="headerCountdown" class="fr-popup__timer">
            <span class="fr-popup__timer-label">{{ headerCountdownLabel }}</span>
            <span class="fr-popup__timer-digits">{{ headerCountdown }}</span>
          </div>
        </div>

        <!-- Unpurchased: show price + buy button -->
        <div v-if="!isPurchased" class="fr-popup__action">
          <div class="fr-popup__price-card">
            <div class="fr-popup__price-main">
              <span class="fr-popup__price-currency">{{ liveCurrency }}</span>
              <span class="fr-popup__price">{{ $formatNumberWithCommas(price) }}</span>
            </div>
            <div v-if="originalPrice" class="fr-popup__price-meta">
              <span class="fr-popup__original-price">{{ liveCurrency }} {{ $formatNumberWithCommas(originalPrice) }}</span>
              <span v-if="discountPercent > 0" class="fr-popup__discount">-{{ discountPercent }}%</span>
            </div>
          </div>
          <button class="fr-popup__btn" :disabled="frPaying || !price" @click="buyGiftPack">
            {{ frPaying ? ($lang.common_loading || 'Loading...') : ctaText }}
          </button>
          <p class="fr-popup__up-to">
            {{ $lang.fr_up_to_prefix || 'Receive up to' }}
            <span class="fr-popup__up-to-percent">200%</span>
            {{ $lang.fr_up_to_suffix || 'rewards.' }}
          </p>
          <p class="fr-popup__disclaimer">{{ $lang.fd_limited_one || 'Limited to one purchase per user.' }}</p>
        </div>

        <!-- Purchased: show 3-day claim list -->
        <div v-else class="fr-popup__action">
          <div class="fr-popup__days">
            <div
              v-for="day in dayList"
              :key="day.index"
              class="fr-popup__day"
              :class="{ 'is-claimable': day.status === 1, 'is-claimed': day.status === 2, 'is-locked': day.status === 0 }"
            >
              <div class="fr-popup__day-label">
                <span class="fr-popup__day-title">{{ ($lang.fr_day || 'Day') + ' ' + day.index }}</span>
                <span v-if="day.amountText" class="fr-popup__day-amount">{{ day.amountText }}</span>
              </div>
              <button
                v-if="day.status === 1"
                class="fr-popup__day-btn is-claim"
                :disabled="claimingDay === day.index"
                @click="claimDay(day.index)"
              >
                {{ claimingDay === day.index ? '...' : ($lang.fd_claim_now || 'Claim') }}
              </button>
              <span v-else-if="day.status === 2" class="fr-popup__day-btn is-done">
                {{ $lang.fr_claimed || 'Claimed' }}
              </span>
              <span v-else class="fr-popup__day-btn is-lock">
                {{ day.availableText || ($lang.fr_locked || 'Locked') }}
              </span>
            </div>
          </div>
          <p class="fr-popup__disclaimer">{{ $lang.fr_daily_hint || 'Return each day to claim your reward.' }}</p>
        </div>
      </div>
    </van-popup>

    <!-- Payment iframe popup -->
    <van-popup
      v-model="frPayShow"
      closeable
      close-icon-position="top-right"
      style="width: 90%; border-radius: 10px; height: 80%"
    >
      <div>
        <iframe
          :src="frPayUrl"
          scrolling="no"
          style="width: 100%; height: 80vh"
          frameborder="0"
        ></iframe>
      </div>
    </van-popup>
    <login v-model="showLogin" overlay></login>
  </div>
</template>

<script>
import { VnRechargeInitS, Pay, ReceiveNewPlayerGiftPack } from '@/api/common'
import { reportPromoPanel } from '@/utils/common'
import { failMessage as rewardFailMessage, hasUnavailableMarker, isRewardClaimSuccessWithProof as rewardClaimSuccess, isNeedLogin, isOkResponse, responseContent } from '@/views/details/activity/rewardApi'
import { activityPaymentUnavailableMessage, selectActivityPaymentMethod } from '@/views/details/activity/activityPayment'
import { hasProvenEmptyActivityPaymentList } from '@/views/details/activity/activityPayment'
import {
  failMessage as paymentFlowFailMessage,
  getPaymentUrl as paymentFlowGetPaymentUrl,
  hasPaymentSuccessProof as paymentFlowHasPaymentSuccessProof,
  isRechargePayReady as isPaymentFlowRechargePayReady
} from '@/components/recharge/paymentFlow'
import { safeGetStorageItem, safeLocalStorage } from '@/utils/safeStorage'

function getLocalStorage() {
  try {
    if (typeof safeLocalStorage === 'function') return safeLocalStorage()
  } catch (e) { return null }
  return null
}

const DEFAULT_ACTIVITY_FAILURE_MESSAGE = 'Reward is temporarily unavailable. Please try again later.'
const DEFAULT_ACTIVITY_PAYMENT_FAILURE_MESSAGE = 'Payment is temporarily unavailable. Please try again later.'
const GENERIC_ACTIVITY_FAILURE_MESSAGES = ['request failed', 'network error', 'networkerror', 'error']

function isGenericActivityFailureMessage(value) {
  const text = String(value == null ? '' : value).trim().toLowerCase()
  return GENERIC_ACTIVITY_FAILURE_MESSAGES.includes(text)
}

function activityFailureFallback(fallback, defaultMessage = DEFAULT_ACTIVITY_FAILURE_MESSAGE) {
  return fallback && !isGenericActivityFailureMessage(fallback) ? fallback : defaultMessage
}

function safeRewardFailMessage(res, fallback, defaultMessage = DEFAULT_ACTIVITY_FAILURE_MESSAGE) {
  const safeFallback = activityFailureFallback(fallback, defaultMessage)
  const message = rewardFailMessage(res, safeFallback)
  return isGenericActivityFailureMessage(message) ? safeFallback : message
}

function safePaymentFailMessage(res, fallback) {
  const safeFallback = activityFailureFallback(fallback, DEFAULT_ACTIVITY_PAYMENT_FAILURE_MESSAGE)
  const message = paymentFlowFailMessage(res, safeFallback)
  return isGenericActivityFailureMessage(message) ? safeFallback : message
}

export default {
  name: 'FirstRechargePopup',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    packData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      frPaying: false,
      frPayShow: false,
      frPayUrl: '',
      claimingDay: 0,
      showLogin: false,
      nowTs: Math.floor(Date.now() / 1000),
      tickTimer: null
    }
  },
  computed: {
    liveToken() {
      return safeGetStorageItem(getLocalStorage(), 'token')
    },
    // 实时国家货币（避免 App 启动早于登录时 mixin 拍下空快照）
    liveCurrency() {
      return safeGetStorageItem(getLocalStorage(), 'currency') || this.getCurrency || ''
    },
    isPurchased() {
      return this.packData && Number(this.packData.purchased) === 1
    },
    price() {
      return (this.packData && this.packData.price) || 0
    },
    originalPrice() {
      return (this.packData && this.packData.originalPrice) || ''
    },
    promoType() {
      return (this.packData && this.packData.promoType) || 'first_recharge_new_1'
    },
    ctaText() {
      return this.$lang.fr_recharge_now || 'Recharge Now'
    },
    discountPercent() {
      const p = Number(this.price) || 0
      const o = Number(this.originalPrice) || 0
      if (!o || o <= p) return 0
      return Math.round(((o - p) / o) * 100)
    },
    headerTargetSec() {
      if (!this.packData) return 0
      const toSec = (t) => {
        const n = Number(t) || 0
        return n > 1e12 ? Math.floor(n / 1000) : n
      }
      if (this.isPurchased) {
        const times = [1, 2, 3]
          .map(i => toSec(this.packData['day' + i + 'AvailableTime']))
          .filter(t => t > this.nowTs)
          .sort((a, b) => a - b)
        return times[0] || 0
      }
      return toSec(this.packData.expireTime)
    },
    headerCountdownLabel() {
      if (this.isPurchased) return this.$lang.fr_next_unlock || 'Next Unlock'
      return this.$lang.fd_critical_deadline || 'Ends In'
    },
    headerCountdown() {
      const target = this.headerTargetSec
      if (!target) return ''
      const diff = target - this.nowTs
      if (diff <= 0) return ''
      const d = Math.floor(diff / 86400)
      const h = Math.floor((diff % 86400) / 3600)
      const m = Math.floor((diff % 3600) / 60)
      const s = diff % 60
      if (d > 0) {
        return d + 'd ' +
          String(h).padStart(2, '0') + ':' +
          String(m).padStart(2, '0') + ':' +
          String(s).padStart(2, '0')
      }
      return (
        String(h).padStart(2, '0') + ':' +
        String(m).padStart(2, '0') + ':' +
        String(s).padStart(2, '0')
      )
    },
    dayList() {
      if (!this.packData) return []
      // 货币始终用本地国家
      const currency = this.liveCurrency || this.packData.currency || ''
      const fmt = (n) => this.$formatNumberWithCommas ? this.$formatNumberWithCommas(n) : n
      const amountFor = (i) => {
        if (i === 1) {
          const v = Number(this.packData.immediateBonus || 0)
          return v ? (currency + ' ' + fmt(v)) : ''
        }
        const min = Number(this.packData['day' + i + 'Min'] || 0)
        const max = Number(this.packData['day' + i + 'Max'] || 0)
        if (!min && !max) return ''
        if (!min || !max || min === max) {
          return currency + ' ' + fmt(min || max)
        }
        return currency + ' ' + fmt(min) + ' ~ ' + fmt(max)
      }
      return [1, 2, 3].map(i => {
        const status = Number(this.packData['day' + i + 'Status'] || 0)
        let availableTime = Number(this.packData['day' + i + 'AvailableTime'] || 0)
        if (availableTime > 1e12) availableTime = Math.floor(availableTime / 1000)
        let availableText = ''
        if (status === 0 && availableTime > 0) {
          const diff = availableTime - this.nowTs
          if (diff > 0) {
            const h = Math.floor(diff / 3600)
            const m = Math.floor((diff % 3600) / 60)
            availableText = h + 'h ' + m + 'm'
          }
        }
        return { index: i, status, availableTime, availableText, amountText: amountFor(i) }
      })
    }
  },
  watch: {
    value(v, oldV) {
      if (v) {
        this.startTick()
        if (!oldV) reportPromoPanel('new_player_giftpack', 1)
      } else {
        this.stopTick()
        if (oldV) reportPromoPanel('new_player_giftpack', 2)
      }
    },
    frPayShow(v, prev) {
      // 支付 iframe 关闭时刷新一次（兼容后台冲正场景）
      if (prev && !v) this.$emit('refresh')
    }
  },
  beforeDestroy() {
    this.stopTick()
  },
  methods: {
    requireLogin() {
      this.showLogin = true
      if (this.$bus) this.$bus.$emit('openLogin')
    },
    failMessage(res, fallback) {
      return safeRewardFailMessage(res, fallback || this.$lang.mc_claim_failed)
    },
    paymentFailMessage(res, fallback) {
      if (typeof paymentFlowFailMessage !== 'function') return this.failMessage(res, fallback)
      return safePaymentFailMessage(res, fallback)
    },
    hasResponseContent(content) {
      return content &&
        typeof content === 'object' &&
        !Array.isArray(content) &&
        Object.keys(content).length > 0
    },
    isNegativeFlag(value) {
      if (value === false || value === 0) return true
      const normalized = String(value).trim().toLowerCase()
      return ['0', 'false', 'fail', 'failed', 'error', 'rejected', 'locked', 'not_receive', 'not_received', 'not_available'].includes(normalized)
    },
    paymentToken(value) {
      return String(value || '').trim().toLowerCase()
    },
    isUnavailablePaymentToken(value) {
      const token = this.paymentToken(value)
      return !!token && (
        ['stub', 'mock', 'mock_fallback', 'mock-fallback', 'mock fallback', 'fallback', 'fallback_used', 'fallback used', 'fallback-used', 'dataunavailable', 'data_unavailable', 'data unavailable', 'data-unavailable', 'read_unavailable', 'readunavailable', 'read unavailable', 'read-unavailable', 'route_unavailable', 'routeunavailable', 'route unavailable', 'route-unavailable', 'technical_error', 'technicalerror', 'technical error', 'technical-error', 'technical_marker', 'technicalmarker', 'technical marker', 'technical-marker', 'backend_failure', 'backendfailure', 'backend failure', 'backend-failure', 'technical_exception', 'technicalexception', 'technical exception', 'technical-exception', 'read_only', 'readonly', 'read only', 'read-only', 'unimplemented', 'not_implemented', 'not implemented', 'not_supported', 'not-supported', 'not supported', 'not_connected', 'not-connected', 'not connected', 'write_blocked', 'writeblocked', 'write blocked', 'write-blocked', 'backend_blocked', 'backendblocked', 'backend blocked', 'backend-blocked', 'blocked', 'unsupported', 'disabled', 'unavailable', 'need_login', 'placeholder', 'about:blank', '#', 'javascript:void(0)'].includes(token) ||
        token.includes('mock') ||
        token.includes('fallback') ||
        token.includes('dataunavailable') ||
        token.includes('data_unavailable') ||
        token.includes('data unavailable') ||
        token.includes('read_unavailable') ||
        token.includes('readunavailable') ||
        token.includes('read unavailable') ||
        token.includes('read-unavailable') ||
        token.includes('route_unavailable') ||
        token.includes('routeunavailable') ||
        token.includes('route unavailable') ||
        token.includes('route-unavailable') ||
        token.includes('technical_error') ||
        token.includes('technicalerror') ||
        token.includes('technical error') ||
        token.includes('technical-error') ||
        token.includes('technical_exception') ||
        token.includes('technical_marker') ||
        token.includes('technicalmarker') ||
        token.includes('technical marker') ||
        token.includes('backend_failure') ||
        token.includes('backendfailure') ||
        token.includes('backend failure') ||
        token.includes('technicalexception') ||
        token.includes('technical exception') ||
        token.includes('technical-exception') ||
        token.includes('技术错误') ||
        token.includes('技术异常') ||
        token.includes('read_only') ||
        token.includes('read only') ||
        token.includes('read-only') ||
        token.includes('unimplemented') ||
        token.includes('not_implemented') ||
        token.includes('not implemented') ||
        token.includes('not_supported') ||
        token.includes('not-supported') ||
        token.includes('not supported') ||
        token.includes('not_connected') ||
        token.includes('not-connected') ||
        token.includes('not connected') ||
        token.includes('write_blocked') ||
        token.includes('writeblocked') ||
        token.includes('write blocked') ||
        token.includes('write-blocked') ||
        token.includes('backend_blocked') ||
        token.includes('backendblocked') ||
        token.includes('backend blocked') ||
        token.includes('backend-blocked') ||
        token.includes('blocked') ||
        token.includes('need_login') ||
        token.includes('unsupported')
      )
    },
    hasBlockedReturnMarker(payload, seen = new Set()) {
      if (!payload || typeof payload !== 'object' || seen.has(payload)) return false
      seen.add(payload)
      return Object.entries(payload).some(([key, value]) => {
        const normalizedKey = String(key || '').trim().toLowerCase()
        if (['failclosed', 'fail_closed', 'falseProof', 'falseproof', 'false_proof', 'paymentStatus', 'paymentstatus', 'payment_status', 'paymentStatusUnconfirmed', 'paymentstatusunconfirmed', 'payment_status_unconfirmed', 'unconfirmed'].includes(normalizedKey) &&
          value !== false &&
          value !== 0 &&
          !['', '0', 'false', 'no', 'off'].includes(this.paymentToken(value))) return true
        if (['rewardproof', 'reward_proof', 'writeproof', 'write_proof'].includes(normalizedKey) &&
          (value === false || value === 0 || ['0', 'false', 'no', 'off'].includes(this.paymentToken(value)))) return true
        if (['readonly', 'read_only', 'readunavailable', 'read_unavailable', 'routeunavailable', 'route_unavailable', 'technicalerror', 'technical_error', 'technicalexception', 'technical_exception', 'technicalmarker', 'technical_marker', 'backendfailure', 'backend_failure', 'mock', 'mock_fallback', 'mockfallback', 'stub', 'fallback', 'fallbackused', 'fallback_used', 'dataunavailable', 'data_unavailable', 'not_supported', 'notsupported', 'not_connected', 'notconnected', 'not_implemented', 'notimplemented', 'blocked', 'backendblocked', 'backend_blocked', 'writeblocked', 'write_blocked', 'failclosed', 'fail_closed', 'unimplemented'].includes(normalizedKey) &&
          (value === true || value === 1 || ['1', 'true', 'yes', 'on', 'mock', 'stub', 'fallback', 'fallback_used', 'data_unavailable', 'read_only', 'readonly', 'read_unavailable', 'readunavailable', 'route_unavailable', 'routeunavailable', 'technical_error', 'technicalerror', 'technical_exception', 'technicalexception', 'technical_marker', 'technicalmarker', 'backend_failure', 'backendfailure', 'not_supported', 'notsupported', 'not_connected', 'notconnected', 'not_implemented', 'notimplemented', 'unimplemented', 'blocked', 'backendblocked', 'backend_blocked', 'writeblocked', 'write_blocked', 'failclosed', 'fail_closed', 'fail closed'].includes(this.paymentToken(value)))) {
          return true
        }
        if (['message', 'msg', 'reason', 'status', 'state'].includes(normalizedKey)) {
          const text = this.paymentToken(value)
          if (text.includes('未接入') || text.includes('mock') || text.includes('stub') || text.includes('fallback') || text.includes('只读') || text.includes('阻止假成功') || text.includes('failclosed') || text.includes('fail closed') || text.includes('fail_closed') || text.includes('falseproof') || text.includes('false proof') || text.includes('false_proof') || text.includes('paymentstatusunconfirmed') || text.includes('payment status unconfirmed') || text.includes('payment_status_unconfirmed') || text.includes('unconfirmed') || text.includes('writeblocked') || text.includes('write blocked') || text.includes('write_blocked') || text.includes('backendblocked') || text.includes('backend blocked') || text.includes('backend_blocked') || text.includes('backendfailure') || text.includes('backend failure') || text.includes('backend_failure') || text.includes('not_connected') || text.includes('not connected') || text.includes('not_supported') || text.includes('not supported') || text.includes('not_implemented') || text.includes('not implemented') || text.includes('routeunavailable') || text.includes('route unavailable') || text.includes('route_unavailable') || text.includes('readunavailable') || text.includes('read unavailable') || text.includes('read_unavailable') || text.includes('technicalerror') || text.includes('technical error') || text.includes('technical_error') || text.includes('technicalmarker') || text.includes('technical marker') || text.includes('technical_marker') || text.includes('technicalexception') || text.includes('technical exception') || text.includes('technical_exception') || text.includes('技术错误') || text.includes('技术异常') || text.includes('unimplemented')) return true
        }
        return value && typeof value === 'object' && this.hasBlockedReturnMarker(value, seen)
      })
    },
    isUsablePaymentMethod(payment) {
      const source = payment && typeof payment === 'object' ? payment : {}
      const key = this.paymentToken(source.paymentKey || source.paymentId)
      const status = this.paymentToken(source.status || source.paymentStatus || source.channelStatus)
      const closeValues = [source.isClose, source.closed, source.close].map(this.paymentToken)
      const enabledValues = [source.enabled, source.enable, source.isEnabled, source.open, source.isOpen]
      if (!key || hasUnavailableMarker(source) || this.isUnavailablePaymentToken(key) || this.isUnavailablePaymentToken(status)) return false
      if (['disabled', 'closed', 'off', 'offline', 'unavailable'].includes(status)) return false
      if (closeValues.some(value => ['1', 'true', 'yes'].includes(value))) return false
      return !enabledValues.some(value => value === false || ['0', 'false', 'no'].includes(this.paymentToken(value)))
    },
    rewardValue(content) {
      const c = content || {}
      return [
        c.amount,
        c.rewardAmount,
        c.awardAmount,
        c.reward,
        c.bonus,
        c.bonusAmount
      ].find(value => value !== undefined && value !== null && value !== '')
    },
    paymentUrl(content) {
      if (typeof paymentFlowGetPaymentUrl === 'function') return this.safePaymentUrl(paymentFlowGetPaymentUrl(content))
      if (!content || typeof content !== 'object') return ''
      return this.safePaymentUrl(content.url ||
        content.payUrl ||
        content.paymentUrl ||
        content.cashierUrl ||
        content.checkoutUrl ||
        content.redirectUrl ||
        content.qrcode ||
        content.qrCode ||
        '')
    },
    isSafeBusinessProof(value) {
      const text = String(value || '').trim()
      if (!text) return false
      const token = this.paymentToken(text)
      return !this.isUnavailablePaymentToken(token) && ![
        '0',
        'none',
        'null',
        'undefined',
        'placeholder',
        'mock',
        'about:blank',
        '#',
        'javascript:void(0)'
      ].includes(token)
    },
    safePaymentUrl(value) {
      const text = String(value || '').trim()
      if (!this.isSafeBusinessProof(text) || text.charAt(0) === '#') return ''
      if (/^[a-z][a-z0-9+.-]*:/i.test(text) && !/^https?:\/\//i.test(text)) return ''
      return text
    },
    hasPaymentFailure(content) {
      if (!content || typeof content !== 'object') return false
      if (hasUnavailableMarker(content) || this.hasBlockedReturnMarker(content)) return true
      const status = String(content.status || content.state || '').trim().toLowerCase()
      return [
        content.paySuccess,
        content.success
      ].some(value => this.isNegativeFlag(value)) ||
        ['fail', 'failed', 'error', 'rejected'].includes(status)
    },
    hasPaymentSuccessProof(res) {
      return paymentFlowHasPaymentSuccessProof(res)
    },
    isPaymentReady(res) {
      const payContent = responseContent(res)
      if (hasUnavailableMarker(res) || this.hasBlockedReturnMarker(res)) return false
      const payUrl = this.paymentUrl(payContent)
      if (!payUrl) return false
      if (!this.hasPaymentSuccessProof(res)) return false
      if (typeof isPaymentFlowRechargePayReady === 'function') {
        return isPaymentFlowRechargePayReady(res) && !!payUrl
      }
      if (!isOkResponse(res) || !this.hasResponseContent(payContent)) return false
      return !this.hasPaymentFailure(payContent)
    },
    isClaimSuccess(res) {
      return rewardClaimSuccess(res) && !hasUnavailableMarker(res) && !this.hasBlockedReturnMarker(res)
    },
    isPaymentInitReady(res, content, selectedPayment) {
      return isOkResponse(res) &&
        !hasUnavailableMarker(res) &&
        !this.hasBlockedReturnMarker(res) &&
        this.hasResponseContent(content) &&
        this.isUsablePaymentMethod(selectedPayment)
    },
    handleClose() {
      this.$emit('input', false)
    },
    startTick() {
      this.stopTick()
      this.nowTs = Math.floor(Date.now() / 1000)
      this.tickTimer = setInterval(() => {
        this.nowTs = Math.floor(Date.now() / 1000)
      }, 1000)
    },
    stopTick() {
      if (this.tickTimer) {
        clearInterval(this.tickTimer)
        this.tickTimer = null
      }
    },
    async buyGiftPack() {
      if (this.frPaying || !this.price) return
      if (!this.liveToken) {
        this.requireLogin()
        return
      }
      this.frPayShow = false
      this.frPayUrl = ''
      this.frPaying = true
      try {
        const initRes = await VnRechargeInitS()
        if (isNeedLogin(initRes)) {
          this.requireLogin()
          return
        }
        const initContent = responseContent(initRes)
        const selectedPayment = selectActivityPaymentMethod(initContent.paymentList || initContent)
        const hasProvenEmptyPayments = typeof hasProvenEmptyActivityPaymentList === 'function' &&
          (hasProvenEmptyActivityPaymentList(initRes) || hasProvenEmptyActivityPaymentList(initContent))
        if (hasProvenEmptyPayments && !selectedPayment && this.hasResponseContent(initContent)) {
          this.$toast({ message: activityPaymentUnavailableMessage(), icon: 'cross' })
          return
        }
        if (!this.isPaymentInitReady(initRes, initContent, selectedPayment)) {
          this.$toast({ message: this.failMessage(initRes, DEFAULT_ACTIVITY_PAYMENT_FAILURE_MESSAGE), icon: 'cross' })
          return
        }
        const payRes = await Pay({
          paymentAmount: this.price,
          paymentId: selectedPayment.paymentId,
          paymentKey: selectedPayment.paymentKey,
          paymentType: selectedPayment.paymentType,
          rechargeFees: selectedPayment.rechargeFees,
          phone: initContent._account,
          promoType: this.promoType
        })
        const payContent = responseContent(payRes)
        if (isNeedLogin(payRes)) {
          this.requireLogin()
        } else if (this.isPaymentReady(payRes)) {
          this.$emit('input', false)
          this.frPayUrl = this.paymentUrl(payContent)
          this.frPayShow = true
          this.$emit('refresh')
        } else {
          this.$toast({ message: this.paymentFailMessage(payRes, DEFAULT_ACTIVITY_PAYMENT_FAILURE_MESSAGE), icon: 'cross' })
        }
      } catch (error) {
        this.$toast({ message: this.failMessage(error, this.$lang.request_failed || 'Request failed. Please try again later.'), icon: 'cross' })
      } finally {
        this.frPaying = false
      }
    },
    async claimDay(dayIndex) {
      if (this.claimingDay) return
      if (!this.liveToken) {
        this.requireLogin()
        return
      }
      this.claimingDay = dayIndex
      try {
        const res = await ReceiveNewPlayerGiftPack({ dayIndex })
        if (isNeedLogin(res)) {
          this.requireLogin()
        } else if (this.isClaimSuccess(res)) {
          const amount = this.rewardValue(responseContent(res))
          const msg = amount
            ? '+' + this.$formatNumberWithCommas(amount) + ' ' + (this.getCurrency || '')
            : (this.$lang.bonus_txt16 || 'Success')
          this.$toast({ message: msg, icon: 'success' })
          this.$emit('refresh')
        } else {
          this.$toast({ message: this.failMessage(res, DEFAULT_ACTIVITY_FAILURE_MESSAGE), icon: 'cross' })
        }
      } catch (error) {
        this.$toast({ message: this.failMessage(error, this.$lang.request_failed || 'Request failed. Please try again later.'), icon: 'cross' })
      } finally {
        this.claimingDay = 0
      }
    }
  }
}
</script>

<style lang="less" scoped>
/* ====== First Recharge Popup ====== */
@fr-primary: @brand;     /* 紫→火焰橙(主色/CTA/激活) */
@fr-accent: #ff8a3d;     /* 紫→火焰橙浅(= --vk-brand-3) */
@fr-yellow: @gold;       /* 黄→金(价格/奖金/金额) */
@fr-green: #15c26b;      /* 绿保留语义(赢/正向),统一 --vk-success */
@fr-red: #f5455c;        /* 红保留语义(错误/亏),统一 --vk-danger */
@fr-bg: @vk-bg;          /* 深页面底 */

.fr-popup-wrapper {
  background: transparent !important;
  overflow: visible !important;
}

.fr-popup {
  position: relative;
  width: 300px;
  background: @fr-bg;
  border: 2px solid fade(@fr-primary, 50%);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.6),
              0 0 24px fade(@fr-primary, 25%);

  &__close {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
    background: none;
    border: none;
    cursor: pointer;
  }

  &__header {
    position: relative;
    padding: 28px 20px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    background: linear-gradient(180deg, fade(@fr-primary, 15%), rgba(15, 23, 42, 0.4));
    overflow: hidden;
  }

  &__grid-bg {
    position: absolute;
    inset: 0;
    background-image: radial-gradient(fade(@fr-accent, 15%) 1px, transparent 1px);
    background-size: 20px 20px;
    opacity: 0.4;
    pointer-events: none;
  }

  &__mission {
    position: relative;
    z-index: 1;
    font-size: var(--vk-fs-xs);
    font-weight: 900;
    color: @fr-yellow;
    letter-spacing: 3px;
    text-transform: uppercase;
    margin-bottom: 10px;
  }

  &__subtitle {
    position: relative;
    z-index: 1;
    font-size: var(--vk-fs-lg);
    font-weight: 900;
    color: rgba(255, 255, 255, 0.9);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 12px;
  }

  &__highlight {
    color: @fr-yellow;
  }

  &__timer {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    background: rgba(0, 0, 0, 0.4);
    padding: 8px 14px;
    border-radius: 10px;
    border: 1px solid fade(@fr-primary, 20%); /* 降噪:倒计时改品牌色,去警报红 */

    &-label {
      font-size: 9px;
      font-weight: 900;
      color: fade(@fr-primary, 80%);
      text-transform: uppercase;
      letter-spacing: 2px;
      margin-bottom: 2px;
    }

    &-digits {
      font-size: var(--vk-fs-2xl);
      font-weight: 900;
      color: @fr-primary;
      letter-spacing: 1px;
      text-shadow: 0 0 12px fade(@fr-primary, 60%);
    }
  }

  &__action {
    padding: 16px 20px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__price-card {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 14px 12px 12px;
    margin-bottom: 14px;
    border-radius: 12px;
    background: linear-gradient(180deg, fade(@fr-yellow, 12%), fade(@fr-primary, 10%));
    border: 1px solid fade(@fr-yellow, 25%);
  }

  &__price-main {
    display: flex;
    align-items: baseline;
    gap: 6px;
  }

  &__price-currency {
    font-size: var(--vk-fs-md);
    font-weight: 900;
    color: @fr-yellow;
  }

  &__price {
    font-size: 36px;
    font-weight: 900;
    color: @fr-yellow;
    line-height: 1;
    letter-spacing: -1px;
    text-shadow: 0 0 12px fade(@fr-yellow, 35%);
  }

  &__price-meta {
    margin-top: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__original-price {
    font-size: var(--vk-fs-sm);
    color: rgba(255, 255, 255, 0.4);
    text-decoration: line-through;
  }

  &__discount {
    font-size: var(--vk-fs-xs);
    font-weight: 900;
    color: var(--vk-bg);
    background: linear-gradient(90deg, #ff5d16, #ff9253);
    padding: 2px 8px;
    border-radius: 6px;
    letter-spacing: 0.5px;
  }

  &__btn {
    width: 100%;
    background: linear-gradient(270deg, @fr-primary, @fr-accent);
    color: var(--vk-bg);
    font-weight: 900;
    font-size: var(--vk-fs-15);
    padding: 12px;
    border-radius: 10px;
    border: none;
    text-transform: uppercase;
    box-shadow: 0 4px 0 #b3360f; /* 火焰橙暗调阴影(原紫 #5a11b3) */
    cursor: pointer;

    &:active {
      box-shadow: none;
      transform: translateY(4px);
    }

    &:disabled {
      opacity: 0.6;
    }
  }

  &__disclaimer {
    margin-top: 10px;
    font-size: 9px;
    color: #475569;
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 1px;
  }

  &__up-to {
    margin-top: 10px;
    text-align: center;
    font-size: var(--vk-fs-sm);
    color: #cbd5e1;
    font-weight: 600;
  }

  &__up-to-percent {
    display: inline-block;
    margin: 0 4px;
    padding: 2px 8px;
    font-size: var(--vk-fs-md);
    font-weight: 900;
    color: @fr-yellow;
    background: fade(@fr-yellow, 15%);
    border: 1px solid fade(@fr-yellow, 45%);
    border-radius: 6px;
    text-shadow: 0 0 6px fade(@fr-yellow, 50%);
  }

  &__days {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__day {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);

    &.is-claimable {
      border-color: @fr-primary; /* 可领取高亮=火焰橙(与领取 CTA 同一主色线) */
      box-shadow: 0 0 10px fade(@fr-primary, 30%);
    }

    &.is-claimed {
      opacity: 0.55;
    }
  }

  &__day-label {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__day-title {
    font-size: var(--vk-fs-sm);
    font-weight: 800;
    color: #ffffff;
  }

  &__day-amount {
    font-size: var(--vk-fs-xs);
    font-weight: 700;
    color: @fr-yellow;
    letter-spacing: 0.3px;
    text-shadow: 0 0 6px fade(@fr-yellow, 25%);
  }

  &__day-btn {
    min-width: 78px;
    text-align: center;
    padding: 6px 12px;
    border-radius: 8px;
    font-size: var(--vk-fs-xs);
    font-weight: 800;
    border: none;
    cursor: pointer;

    &.is-claim {
      background: @fr-primary; /* 领取按钮=火焰橙 CTA(铁律①) */
      color: var(--vk-bg);
    }

    &.is-done {
      background: rgba(255, 255, 255, 0.06);
      color: rgba(255, 255, 255, 0.5);
    }

    &.is-lock {
      background: rgba(255, 255, 255, 0.04);
      color: rgba(255, 255, 255, 0.35);
      font-size: var(--vk-fs-xs);
    }

    &:disabled {
      opacity: 0.6;
    }
  }
}
</style>
